import { computed, onBeforeUnmount, ref, toValue, watch } from 'vue';
import type { MaybeRefOrGetter } from 'vue';

interface UseResendCooldownOptions {
  active?: MaybeRefOrGetter<boolean | undefined>;
  cooldownSeconds?: MaybeRefOrGetter<number | undefined>;
  resetKey?: MaybeRefOrGetter<number | string | undefined>;
}

const DEFAULT_COOLDOWN_SECONDS = 20;

export const useResendCooldown = ({
  active = true,
  cooldownSeconds = DEFAULT_COOLDOWN_SECONDS,
  resetKey = 0,
}: UseResendCooldownOptions = {}) => {
  const secondsRemaining = ref(0);

  let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  const clearCooldown = () => {
    if (countdownIntervalId === null) {
      return;
    }

    clearInterval(countdownIntervalId);
    countdownIntervalId = null;
  };

  const cooldownDurationSeconds = computed(() => {
    return Math.max(0, Math.round(toValue(cooldownSeconds) ?? DEFAULT_COOLDOWN_SECONDS));
  });

  const isActive = computed(() => {
    return toValue(active) ?? true;
  });

  const restartCooldown = () => {
    clearCooldown();

    secondsRemaining.value = cooldownDurationSeconds.value;

    if (secondsRemaining.value === 0) {
      return;
    }

    countdownIntervalId = setInterval(() => {
      if (secondsRemaining.value <= 1) {
        secondsRemaining.value = 0;
        clearCooldown();
        return;
      }

      secondsRemaining.value -= 1;
    }, 1000);
  };

  watch(
    isActive,
    (nextIsActive) => {
      if (!nextIsActive) {
        clearCooldown();
        secondsRemaining.value = 0;
        return;
      }

      restartCooldown();
    },
    { immediate: true },
  );

  watch(
    () => toValue(resetKey),
    (nextResetKey, previousResetKey) => {
      if (!isActive.value || nextResetKey === previousResetKey) {
        return;
      }

      restartCooldown();
    },
  );

  onBeforeUnmount(() => {
    clearCooldown();
  });

  const isCooldownActive = computed(() => {
    return secondsRemaining.value > 0;
  });

  return {
    secondsRemaining,
    isCooldownActive,
    restartCooldown,
  };
};
